import { ContentType } from '@amzn/awscat-pattern-library-service-model';
import { Editor, Extensions, TiptapEditingExtensions, TiptapOutputType } from '@amzn/awscat-editor';
import { log } from '../../loggers/Logger';
import { AsyncAdhocMigration } from '../AdhocMigration';
import { ContentEditorService } from '../ContentEditorService';
import { enumeratePages } from '../PageOperations';
import { ContentPatcher } from './ContentPatcher';
import { hasAnyMissingNodeId } from './NodeIdVerifier';

export interface TiptapEditorInstance {
  /**
   * Uses tiptap editor to parse the provided content and return the parsed
   * value. This allows the UniqueId extension to add missing id for nodes.
   * Returns `false` when the content is invalid and cannot be loaded.
   */
  tryParse(value: TiptapOutputType): TiptapOutputType | false;

  /**
   * Removes the Tiptap Editor instance and related DOM elements where
   * it attached to.
   */
  dispose(): void;
}

/** Create a reusable Tiptap parser backed by a single mounted editor. */
export async function createTiptapEditorInstance(
  extensions: Extensions,
): Promise<TiptapEditorInstance> {
  // Off-screen but attached — some Tiptap plugins (e.g. UniqueID) only run
  // their appendTransaction hooks against a view mounted in the live DOM.
  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.left = '-10000px';
  host.style.top = '0';
  host.setAttribute('aria-hidden', 'true');
  document.body.appendChild(host);

  const editor = await new Promise<Editor>((resolve) => {
    new Editor({
      element: host,
      extensions: extensions as any,
      content: '',
      editable: false,
      onCreate: ({ editor }) => resolve(editor),
    });
  });

  return {
    tryParse(value) {
      // Parse silently: nothing listens for 'update' on this off-screen editor,
      // and Tiptap v3 would otherwise emit it by default (v2 did not).
      try {
        editor.commands.setContent(value as string | object, { emitUpdate: false });
      } catch (err) {
        log.warn({ err }, 'Invalid content detected; skipping parse');
        return false;
      }
      return editor.getJSON();
    },
    dispose() {
      editor.destroy();
      host.remove();
    },
  };
}

/**
 * Runs every node in every page of a guide through the TipTap editor so missing
 * node IDs get generated. Needed for legacy content authored before the TipTap
 * unique-id extension was introduced, whose nodes have no IDs assigned.
 */
export class GuideMissingNodeIdsMigration implements AsyncAdhocMigration {
  readonly kind = 'async' as const;
  readonly name = 'guide-missing-node-ids-migration';

  private logger = log.child({ class: this.constructor.name, migration: this.name });

  async run(editor: ContentEditorService): Promise<void> {
    // Guard: editor must be open and editable.
    const initialHandle = editor.loadedDocumentHandle;
    if (!initialHandle || !editor.isEditable) {
      this.logger.info('Skipping: editor is not open in an editable mode');
      return;
    }
    const contentId = initialHandle.contentId;
    const initialContent = editor.content;
    if (!initialContent) {
      this.logger.info({ contentId }, 'Skipping: editor has no content');
      return;
    }

    // Guides only — Strategies and Patterns don't lazy-load pages.
    if (initialContent.contentMetadata.contentType !== ContentType.Guide) {
      return;
    }

    // Skip early if every UniqueId-tracked node already has an id. Avoids the
    // cost of mounting a Tiptap editor for already-migrated Guides.
    if (!hasAnyMissingNodeId(initialContent)) {
      this.logger.info({ contentId }, 'Skipping: all Guide nodes already have ids');
      return;
    }

    // Create an instance of Tiptap Editor. We'd need to declare its extensions.
    // Use the pre-configured TiptapEditingExtensions from the awscat-editor package.
    const tiptapEditor = await createTiptapEditorInstance(
      TiptapEditingExtensions({
        // provide dummy upload callback, we don't expect this being used.
        imageUpload: (_file) => Promise.resolve(''),
        // assume all links are valid
        validateLink: () => true,
        // provide a dummy image
        fetchImage: async (_srcUrl: string): Promise<string> => Promise.resolve('dummy'),
        entities: editor.getValidationEntities(),
        comments: [],
        enableSingleLine: false,
        useCustomImageTag: true,
        enableUniqueId: true,
      }),
    );

    // Run each page/section body through the Tiptap editor and collect the
    // patched results in a ContentPatcher, keyed by page or section id.
    const patcher = new ContentPatcher();
    const liveContent = editor.content;

    // Search for Tiptap Editor nodes in the content and run it through
    // Tiptap Editor instance
    try {
      // Stop if the user has switched to a different content (rare, but possible)
      if (!liveContent || editor.loadedDocumentHandle?.contentId !== contentId) {
        this.logger.info({ contentId }, 'Aborting stage: editor closed or switched contents');
        return;
      }
      if (!editor.isEditable) {
        this.logger.info({ contentId }, 'Aborting stage: editor is no longer editable');
        return;
      }

      for (const page of enumeratePages(liveContent)) {
        // Parse the page body. Skip when the editor rejects the content.
        if (page.contentData) {
          const parsed = tiptapEditor.tryParse(page.contentData);
          if (parsed !== false) {
            patcher.patchPage(page.id, parsed);
          }
        }

        // Parse each section body under the page. Skip when the editor rejects the content.
        for (const section of page.contentSections ?? []) {
          if (section.contentData) {
            const parsed = tiptapEditor.tryParse(section.contentData);
            if (parsed !== false) {
              patcher.patchSection(page.id, section.id, parsed);
            }
          }
        }
      }
    } finally {
      tiptapEditor.dispose();
    }

    // Stop if no pages were parsed
    if (patcher.size === 0) {
      return;
    }

    // Apply the patches to the content and stage the edit.
    editor.stageEdit(patcher.getPatched(liveContent));

    this.logger.info(
      { contentId, parsedSectionsSize: patcher.size },
      'Staged normalized Guide pages',
    );
  }
}
